import type { AssetRequestOptions } from './AssetContracts';

interface Job {
  readonly priority: number;
  readonly start: () => Promise<void>;
  readonly cancel: () => void;
}

/** Shared scheduling primitive; decoders and GPU resource ownership stay in their subsystems. */
export class PreparationScheduler {
  readonly #queue: Job[] = [];
  readonly #lifetime = new AbortController();
  #running = 0;

  constructor(readonly concurrency = 1) {
    if (!Number.isInteger(concurrency) || concurrency < 1) throw new Error('Invalid preparation concurrency.');
  }

  schedule<T>(prepare: (signal: AbortSignal) => Promise<T>, options: AssetRequestOptions = {}): Promise<T> {
    const signal = options.signal
      ? AbortSignal.any([options.signal, this.#lifetime.signal]) : this.#lifetime.signal;
    if (signal.aborted) return Promise.reject(signal.reason);
    return new Promise<T>((resolve, reject) => {
      const job: Job = {
        priority: options.priority ?? 0,
        cancel: () => {
          const index = this.#queue.indexOf(job);
          if (index >= 0) this.#queue.splice(index, 1);
          reject(signal.reason);
        },
        start: async () => {
          signal.removeEventListener('abort', job.cancel);
          try {
            signal.throwIfAborted();
            // Active jobs retain their slot until cleanup completes, even after cancellation.
            resolve(await prepare(signal));
          } catch (error) { reject(error); }
        },
      };
      signal.addEventListener('abort', job.cancel, { once: true });
      this.#queue.push(job);
      this.#queue.sort((left, right) => right.priority - left.priority);
      this.#pump();
    });
  }

  dispose(): void { this.#lifetime.abort(); }

  #pump(): void {
    while (this.#running < this.concurrency && this.#queue.length) {
      const job = this.#queue.shift()!;
      this.#running += 1;
      void job.start().finally(() => { this.#running -= 1; this.#pump(); });
    }
  }
}

export const gpuPreparationScheduler = new PreparationScheduler();
